import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { graphqlApi } from '../../../services/graphql';
import { useAuthStore } from '../../../store/authStore';
import { generateCampaignContractPdf } from '../../../utils/campaignContractPdf';

interface VisitInfo {
  startDate?: string;
  endDate?: string;
  adults?: number;
  children?: number;
  infants?: number;
}

interface MatchItem {
  matchId: string;
  campaignId: string;
  status: string;
  appliedAt: string;
  rewardAmount?: number;
  // Contract rendering needs these — only populated once the match has been
  // requested/accepted so they're optional on the type.
  selectedMissions?: string[];
  visitInfo?: VisitInfo;
  requestedAt?: string;
  respondedAt?: string;
}

interface CampaignData {
  campaignId: string;
  title?: string;
  campaignName?: string;
  description?: string;
  providedValueDescription?: string;
  campaignCategory?: string;
  category?: string;
  budgetRange?: string;
  brandName?: string;
  numberOfRecruits?: number;
  campaignType?: string;
  contentTypes?: string[];
  imageUrl?: string;
  representativeImages?: { fileUrl: string }[];
  // Fields consumed by the 캠페인 계약서 PDF generator. The Campaign row
  // carries more data than we explicitly type here; the contract helper
  // reads a broader `any` shape. These are just the ones we reference
  // directly when calling it.
  productName?: string;
  rewardAmount?: number;
  visitStartDate?: string;
  visitEndDate?: string;
  uploadDeadlineDays?: number;
  contentWritingRequirements?: string;
  publishingConditions?: any;
  secondarySubmissions?: { type: string; quantity: number; note?: string }[];
}

type FilterTab = 'all' | 'shortlisted' | 'accepted' | 'canceled' | 'completed';

const MyCampaignsPage: React.FC = () => {
  const { t } = useTranslation(['profile', 'campaigns']);
  const { user } = useAuthStore();

  const [matches, setMatches] = useState<MatchItem[]>([]);
  const [campaigns, setCampaigns] = useState<Record<string, CampaignData>>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  const fetchData = useCallback(async () => {
    if (!user?.userId) return;
    try {
      setLoading(true);
      // Fetch matches
      const matchRes = await graphqlApi.getMatches({ userId: user.userId });
      const matchList: MatchItem[] = matchRes.success && matchRes.data?.matches
        ? matchRes.data.matches.map((m: any) => ({
            matchId: m.matchId, campaignId: m.campaignId,
            status: m.status, appliedAt: m.appliedAt,
            rewardAmount: m.rewardAmount,
            selectedMissions: m.selectedMissions || [],
            visitInfo: m.visitInfo || undefined,
            requestedAt: m.requestedAt || undefined,
            respondedAt: m.respondedAt || undefined,
          }))
        : [];
      setMatches(matchList);

      // Fetch all campaigns to get details
      const campaignRes = await graphqlApi.getCampaigns({});
      if (campaignRes.success && campaignRes.data) {
        const campaignList = (campaignRes.data as any).campaigns || campaignRes.data;
        const map: Record<string, CampaignData> = {};
        if (Array.isArray(campaignList)) {
          campaignList.forEach((c: any) => { map[c.campaignId] = c; });
        }
        setCampaigns(map);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getFiltered = () => {
    switch (activeTab) {
      case 'shortlisted': return matches.filter(m => m.status === 'shortlisted' || m.status === 'requested');
      case 'accepted': return matches.filter(m => m.status === 'accepted');
      case 'canceled': return matches.filter(m => m.status === 'canceled' || m.status === 'rejected' || m.status === 'expired');
      case 'completed': return matches.filter(m => m.status === 'completed');
      default: return matches;
    }
  };

  const counts = {
    all: matches.length,
    shortlisted: matches.filter(m => m.status === 'shortlisted' || m.status === 'requested').length,
    accepted: matches.filter(m => m.status === 'accepted').length,
    canceled: matches.filter(m => m.status === 'canceled' || m.status === 'rejected' || m.status === 'expired').length,
    completed: matches.filter(m => m.status === 'completed').length,
  };

  const filtered = getFiltered();
  const tabs: { key: FilterTab; labelKey: string }[] = [
    { key: 'all', labelKey: 'myProfile.myCampaigns.all' },
    { key: 'shortlisted', labelKey: 'myProfile.myCampaigns.shortlisted' },
    { key: 'accepted', labelKey: 'myProfile.myCampaigns.accepted' },
    { key: 'canceled', labelKey: 'myProfile.myCampaigns.canceled' },
    { key: 'completed', labelKey: 'myProfile.myCampaigns.completed' },
  ];

  const krwFormatter = new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="pt-6 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">
          {t('profile:myProfile.myCampaigns.title')}
        </h2>
        <hr className="border-gray-200" />
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === tab.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t(`profile:${tab.labelKey}`)} ({counts[tab.key]})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {t('profile:myProfile.myCampaigns.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((match) => {
            const c = campaigns[match.campaignId];
            const imgUrl = c?.representativeImages?.[0]?.fileUrl || c?.imageUrl;
            const title = c?.campaignName || c?.title || '';
            const desc = c?.providedValueDescription || c?.description || '';
            return (
              <div key={match.matchId}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
                {/* Image Header */}
                <div className="relative h-48 overflow-hidden">
                  {imgUrl ? (
                    <>
                      <img src={imgUrl} alt={title} className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black bg-opacity-30" />
                    </>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600" />
                  )}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold rounded-full shadow-sm">
                      {c?.campaignCategory || c?.category || ''}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full shadow-sm ${
                      match.status === 'applied' ? 'bg-gray-100 text-gray-700' :
                      match.status === 'shortlisted' ? 'bg-indigo-100 text-indigo-700' :
                      match.status === 'requested' ? 'bg-yellow-100 text-yellow-700' :
                      match.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      match.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      match.status === 'expired' ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {t(`profile:myProfile.myCampaigns.status.${match.status}`)}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                    <h3 className="text-lg font-semibold text-white line-clamp-2">{title}</h3>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{desc}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    {c?.brandName && (
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
                        </svg>
                        {c.brandName}
                      </span>
                    )}
                    {c?.numberOfRecruits != null && (
                      <span>{c.numberOfRecruits}명 모집</span>
                    )}
                  </div>
                  {c?.contentTypes && c.contentTypes.length > 0 && (
                    <div className="flex gap-2 mb-4 flex-wrap">
                      {c.contentTypes.slice(0, 3).map((ct, idx) => {
                        const keyMap: Record<string, string> = {
                          instagram_post: 'instagramPost', instagram_reels: 'instagramReels',
                          youtube: 'youtube', tiktok: 'tiktok', naver_blog: 'naverBlog',
                        };
                        return (
                          <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded">
                            {t(`campaigns:registration.contentTypes.${keyMap[ct] || ct}`, ct)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {/* Reward */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      {t('profile:myProfile.myCampaigns.reward', '리워드')}
                    </span>
                    {typeof match.rewardAmount === 'number' ? (
                      <span className="text-sm font-semibold text-gray-900">
                        {krwFormatter.format(match.rewardAmount)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">
                        {t('profile:myProfile.myCampaigns.rewardUnavailable')}
                      </span>
                    )}
                  </div>
                  {/* Application Status */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${
                          match.status === 'applied' ? 'bg-gray-500' :
                          match.status === 'shortlisted' ? 'bg-indigo-500' :
                          match.status === 'requested' ? 'bg-yellow-500' :
                          match.status === 'accepted' ? 'bg-green-500' :
                          match.status === 'completed' ? 'bg-blue-500' :
                          match.status === 'rejected' ? 'bg-red-500' :
                          match.status === 'expired' ? 'bg-orange-500' :
                          'bg-gray-400'
                        }`} />
                        <span className="text-sm font-medium text-gray-700">
                          {t(`profile:myProfile.myCampaigns.status.${match.status}`)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {t('profile:myProfile.myCampaigns.appliedAt')}: {new Date(match.appliedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  {match.status === 'requested' && (
                    <Link
                      to={`/my-profile/campaigns/accept/${match.matchId}`}
                      className="block w-full text-center px-4 py-2 mb-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                    >
                      {t('profile:myProfile.myCampaigns.confirmCampaign', '캠페인 진행 확정')}
                    </Link>
                  )}
                  {match.status === 'accepted' && (
                    <Link
                      to={`/my-profile/campaigns/submit/${match.matchId}`}
                      className="block w-full text-center px-4 py-2 mb-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors"
                    >
                      {t('profile:myProfile.myCampaigns.submitContent', '콘텐츠 제출')}
                    </Link>
                  )}
                  {(match.status === 'accepted' || match.status === 'completed') && (
                    <button
                      type="button"
                      onClick={() => {
                        // Render the 캠페인 계약서 PDF for this match. Requires
                        // publishingConditions from the Campaign and selectedMissions
                        // + visitInfo captured when the influencer accepted.
                        if (!c) return;
                        const visit = match.visitInfo || {};
                        generateCampaignContractPdf({
                          campaign: c,
                          userName: user?.name || '',
                          selectedMissions: match.selectedMissions || [],
                          visitStartDate: visit.startDate || c.visitStartDate || '',
                          visitEndDate: visit.endDate || c.visitEndDate || '',
                          requestedAt: match.requestedAt,
                          respondedAt: match.respondedAt,
                        });
                      }}
                      className="block w-full text-center px-4 py-2 mb-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                    >
                      {t('profile:myProfile.myCampaigns.viewContract', '캠페인 계약서 보기')}
                    </button>
                  )}
                  <Link
                    to={`/campaigns/${match.campaignId}`}
                    className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                  >
                    {t('profile:myProfile.myCampaigns.viewDetails', 'View Details')}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyCampaignsPage;
